import { useConfig } from "@gik/api/config/config";
import { useDevStore } from "@gik/core/store/DevStore";
import { useUserStore } from "@gik/core/store/UserStore";

/**
 * Visibility rule for the Corp (PG Corp / Howl Tools) top nav bar (GIK-11069).
 *
 * Returns `true` only when a signed-in Give InKind user's account is linked to
 * PG Corp. This is a server-side fact, surfaced as the `pgcorp` boolean on
 * `GET /api/v2/config` (shipped by GIK-11070) — never inferred from a referrer,
 * cookie, or query param, and deliberately not carried in the JWT.
 *
 * Consequences, all intentional (see GIK-11069 spec):
 * - Signed-out visitors never see the bar (no user, nothing to check).
 * - A linked user sees it on every session, not just the Howl Tools handoff,
 *   because the link lives on the account, not the session.
 * - Logging out removes the user, so the bar disappears with it.
 *
 * Data flow: the `config` request is already made once at app boot
 * (`AppImports` -> `useConfig`) and cached by SWR under the `config` key, so
 * calling `useConfig` here subscribes to that same cache rather than issuing a
 * second request.
 *
 * Until GIK-11070 lands there is no real flag to test with, so a dev-only
 * override (`forcePgCorp` in `DevStore`) stands in for the account-link fact.
 * It only substitutes the `pgcorp` signal — the signed-in requirement still
 * holds, so previewing means previewing as a signed-in user, matching prod.
 */
export function useIsPgCorpUser(): boolean {
	const isSignedIn = useUserStore((store) => Boolean(store.id));
	const { data } = useConfig();
	const forcePgCorp = useDevStore((store) => store.forcePgCorp);

	if (!isSignedIn) return false;

	return Boolean(data?.pgcorp) || Boolean(forcePgCorp);
}
